import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle, Plus, Trash2, Layers } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import * as Mutation from '../../../api/gql/mutation'
import ConfirmDialog from './ConfirmDialog'
import { describeScheduleError } from '../lib/errors'
import { formatIsoDate, todayIso } from '../lib/format'
import type { ScheduleBaseline } from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  scheduleId: string
  baselines: ScheduleBaseline[]
  activeBaselineId: string | null
  /** Review fix (Minor #8, spec §10): an archived schedule hides every editing affordance — the
   * dialog stays open as a historical view (activate radio, delete, new-baseline row all hidden). */
  readOnly?: boolean
  onChanged?: () => void
}

const NONE = 'none'

/** Baseline management dialog: activate/deactivate (radio), save a new snapshot, delete one. Spec B §9.9. */
const BaselinesDialog: React.FC<Props> = ({ open, onOpenChange, scheduleId, baselines, activeBaselineId, readOnly, onChanged }) => {
  const { t } = useTranslation()
  const defaultName = () => t('Schedule_baseline_default_name', { n: baselines.length + 1, date: formatIsoDate(t, todayIso()) })
  const [name, setName] = useState(defaultName)
  const [deleting, setDeleting] = useState<ScheduleBaseline | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  // Re-seed the default name whenever the baseline count changes (created/deleted) so the
  // suggested "next" name stays accurate.
  useEffect(() => { setName(defaultName()) }, [baselines.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const [createBaseline, { loading: creating }] = useMutation(Mutation.CREATE_SCHEDULE_BASELINE)
  const [setActiveBaseline, { loading: activating }] = useMutation(Mutation.SET_SCHEDULE_ACTIVE_BASELINE)
  const [deleteBaseline] = useMutation(Mutation.DELETE_SCHEDULE_BASELINE)

  const handleActivate = async (value: string) => {
    try {
      await setActiveBaseline({ variables: { scheduleId, baselineId: value === NONE ? null : value } })
      onChanged?.()
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) }
  }

  const handleCreate = async () => {
    try {
      await createBaseline({ variables: { scheduleId, name: name.trim() } })
      toastr.success('', t('Schedule_baseline_created'))
      onChanged?.()
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) }
  }

  const runDelete = async () => {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await deleteBaseline({ variables: { scheduleId, id: deleting.id } })
      toastr.success('', t('Schedule_baseline_deleted'))
      setDeleting(null)
      onChanged?.()
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) } finally { setDeleteBusy(false) }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-primary" />{t('Schedule_baselines')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          {(() => {
            const table = (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    {!readOnly && <th className="py-1 w-8" />}
                    <th className="py-1">{t('Schedule_field_name')}</th>
                    <th className="py-1">{t('Schedule_baseline_created_by')}</th>
                    <th className="py-1">{t('Schedule_baseline_col_date')}</th>
                    <th className="py-1 text-right">{t('Schedule_baseline_col_activities')}</th>
                    {!readOnly && <th className="py-1 w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {!readOnly && (
                    <tr className="border-t">
                      <td className="py-2 align-top"><RadioGroupItem value={NONE} id="baseline-none" /></td>
                      <td className="py-2 align-top text-muted-foreground" colSpan={4}>
                        <Label htmlFor="baseline-none" className="cursor-pointer font-normal">{t('Schedule_baseline_none')}</Label>
                      </td>
                      <td />
                    </tr>
                  )}
                  {baselines.map((b) => (
                    <tr key={b.id} className="border-t">
                      {!readOnly && <td className="py-2 align-top"><RadioGroupItem value={b.id} id={`baseline-${b.id}`} /></td>}
                      <td className="py-2 align-top">
                        {readOnly ? (
                          <span className="inline-flex items-center gap-2">
                            {b.name}
                            {b.id === activeBaselineId && <Badge variant="success">{t('Schedule_baseline_active')}</Badge>}
                          </span>
                        ) : (
                          <Label htmlFor={`baseline-${b.id}`} className="cursor-pointer font-normal inline-flex items-center gap-2">
                            {b.name}
                            {b.id === activeBaselineId && <Badge variant="success">{t('Schedule_baseline_active')}</Badge>}
                          </Label>
                        )}
                      </td>
                      <td className="py-2 align-top text-muted-foreground">{b.createdBy?.name ?? '—'}</td>
                      <td className="py-2 align-top text-muted-foreground tabular-nums">{new Date(b.createdAt).toLocaleDateString(t('date_locale') || 'en-US')}</td>
                      <td className="py-2 align-top text-right tabular-nums">{b.activityCount}</td>
                      {!readOnly && (
                        <td className="py-2 align-top text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleting(b)} aria-label={t('Common_delete')}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
            return readOnly ? table : (
              <RadioGroup value={activeBaselineId ?? NONE} onValueChange={handleActivate} disabled={activating}>
                {table}
              </RadioGroup>
            )
          })()}
          {!readOnly && activating && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground"><LoaderCircle className="h-3 w-3 animate-spin" />{t('Common_saving')}</p>
          )}
        </div>

        {!readOnly && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" />
            <Button size="sm" className="h-8 shrink-0" onClick={handleCreate} disabled={creating || !name.trim()}>
              {creating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('Schedule_baseline_save')}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-end pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_close')}</Button>
        </div>

        {!readOnly && (
          <ConfirmDialog
            open={!!deleting}
            title={t('Schedule_baseline_delete_title')}
            description={deleting ? t('Schedule_baseline_delete_body', { name: deleting.name }) : undefined}
            confirmLabel={t('Common_delete')}
            variant="destructive"
            submitting={deleteBusy}
            onCancel={() => setDeleting(null)}
            onConfirm={runDelete}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
export default BaselinesDialog
